// ЖИВЫЕ КАРТОЧКИ КОМПАНИИ, КОНТАКТА И СУДНА: лента, комментарии, свои поля.
//
// Чем это отличается от api/cardLive.ts. Тот — точка стыковки карточки СДЕЛКИ (её ручки
// записи другие: /api/deal/update — write-through в Bitrix). Здесь — три справочные
// карточки, у которых живая часть целиком в СВОЕЙ схеме ядра:
//   лента       GET  /api/cards/feed?entity=&id=&limit=&offset=   (card_feed.py)
//   свои поля   GET  /api/cards/fields?entity=&id=                (cards_fields.py)
//   комментарий POST /api/cards-write/comment-add                 {entity, id, text}
//   значение    POST /api/cards-write/field-value-save            {entity, id, code, value}
// В Bitrix этим путём не пишется ничего: слой записи ядра работает только со своей схемой.
//
// КАКИМ ID ЖИВЁТ КАРТОЧКА. У ленты и полей id — номер строки СВОЕЙ схемы (company.id,
// person.id, vessel.id), а не номер Bitrix: адрес карточки (/companies/9955) — это номер
// Bitrix, и подставлять его в feed нельзя — пространства номеров пересекаются
// (см. _наружу_id в crm_own_api.py). Свой номер приезжает вместе с карточкой:
// GET /own/company|contact|vessel кладёт его в `_OWN.id` (проверено пробой 30.07.2026).
// Отдельного запроса ради него не делаем: когда включено зеркало (src=mirror), `_OWN`
// в ответе нет — и лента честно говорит, что живёт в своей базе, а не догадывается.
//
// АВТОРА ЗДЕСЬ НЕТ. Личность знает только BFF: он подставляет author_bitrix из сессии
// и затирает всё, что могло прийти из браузера (web/bff/main.py, cards_write_route).
// Поэтому в телах запросов нет ни author, ни author_login — их отправка была бы либо
// бесполезной, либо попыткой представиться.

import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import type { CardField } from '../components/card/FieldRow'

import { Unauthorized } from './client'
import { источник } from './istochnik'

/** Страница ленты — как PAGE в cards_api.py. */
export const СТРАНИЦА_ЛЕНТЫ = 50

/** Русские коды сущностей — язык /cards/* ядра (cards_fields.СУЩНОСТИ_ПОЛЕЙ). */
export type СущностьКарточки = 'компания' | 'контакт' | 'судно'

// ─────────────────────────── формы ответов ядра ───────────────────────────

/** Строка ленты — форма /cards/feed (card_feed.py, проверено пробой на живых id). */
export interface СтрокаЛенты {
  id: string | number
  origin?: 'crm' | 'bitrix'
  /** comment | field | stage | system (вехи-сделки) | activity (дела Bitrix). */
  kind: string
  /** ISO-время; у вех-сделок — день. */
  ts?: string | null
  /** Автор объектом {id, name} или null (у вех). */
  author?: { id?: unknown; name?: string | null } | null
  text?: string | null
  data?: Record<string, unknown> | null
}

export interface СтраницаЛенты {
  rows: СтрокаЛенты[]
  total: number
  limit: number
  offset: number
  /** Смещение следующей страницы. Считается здесь: ядро отдаёт только total. */
  next: number | null
}

/** Своё поле карточки — определение из конструктора (cards_fields._строка_определения). */
export interface СвоёПоле {
  id: string
  entity: string
  code: string
  label: string
  /** Ровно виды FieldRow — совпадение не случайно: ВИДЫ в cards_fields.py те же слова. */
  kind: 'text' | 'number' | 'date' | 'select' | 'checkbox' | 'link'
  options: string[]
  group: string | null
  sort: number
  required: boolean
  archived: boolean
}

export interface НашиПоля {
  custom: СвоёПоле[]
  /** Код поля → записанное значение (field_value). Пусто — значений ещё нет. */
  values: Record<string, unknown>
}

// ─────────────────────────── свой id и словá про его отсутствие ───────────────────────────

/**
 * Свой номер карточки из уже загруженной записи: `_OWN.id` в ответе /own/company,
 * /own/contact и /own/vessel. Нет — null, и это ответ, а не ошибка: либо читаем зеркало
 * (там `_OWN` не бывает), либо запись в свою базу ещё не переносили.
 */
export function ownIdКарточки(запись: Record<string, unknown> | null | undefined): number | null {
  const own = (запись as { _OWN?: { id?: unknown } } | null | undefined)?._OWN
  const id = Number(own?.id)
  return Number.isFinite(id) && id > 0 ? id : null
}

export const НЕ_ПЕРЕНЕСЕНА =
  'Карточка ещё не перенесена в свою базу — лента и свои поля появятся после переноса.'

export const ЧИТАЕМ_ЗЕРКАЛО =
  'Включено чтение из зеркала Bitrix (src=mirror): лента и свои поля живут в своей базе '
  + 'и здесь не показываются.'

/** 404 ядра по ленте/полям: записи с таким своим id в схеме нет. */
export class КарточкаНеПеренесена extends Error {
  constructor() {
    super(НЕ_ПЕРЕНЕСЕНА)
    this.name = 'КарточкаНеПеренесена'
  }
}

// ─────────────────────────── транспорт ───────────────────────────

/**
 * Разбор ответа /api/cards[-write]/*. Каждый вид отказа — своими словами:
 *   404 not_found → КарточкаНеПеренесена (это состояние карточки, а не поломка);
 *   501 no_layer  → «слоя в схеме ещё нет» — словами ядра;
 *   403           → отказ прав BFF, текст из `why`;
 *   400 refused   → отказ слоя записи, тексты написаны для человека и не пересказываются.
 */
async function разобрать<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  let данные: unknown = {}
  try {
    данные = текст ? JSON.parse(текст) : {}
  } catch {
    данные = {}
  }
  const т = (данные ?? {}) as Record<string, unknown> & { fields?: Record<string, string> }
  if (res.status === 404) throw new КарточкаНеПеренесена()
  if (res.status === 501 || т.error === 'no_layer') {
    throw new Error(String(т.why ?? 'этого слоя в своей схеме ещё нет'))
  }
  if (!res.ok) {
    // Отказ по полю (fields: {код: что не так}) точнее общего `why` — он и уходит к строке.
    const поПолю = т.fields && Object.values(т.fields)[0]
    throw new Error(String(поПолю ?? т.why ?? т.error ?? `HTTP ${res.status}`))
  }
  return данные as T
}

const читать = async <T>(путь: string): Promise<T> =>
  разобрать<T>(await fetch(путь, { credentials: 'same-origin' }))

const писать = async <T>(ручка: string, тело: Record<string, unknown>): Promise<T> =>
  разобрать<T>(await fetch(`/api/cards-write/${ручка}`, {
    method: 'POST',
    credentials: 'same-origin',              // личность знает только BFF, кука его
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(тело),
  }))

/** Лента карточки, одна страница. Свежие первыми — так отдаёт ядро. */
export async function лентаКарточки(
  сущность: СущностьКарточки, ownId: number, offset = 0,
): Promise<СтраницаЛенты> {
  const п = new URLSearchParams({
    entity: сущность, id: String(ownId),
    limit: String(СТРАНИЦА_ЛЕНТЫ), offset: String(offset),
  })
  const о = await читать<{ rows?: СтрокаЛенты[]; total?: number; limit?: number; offset?: number }>(
    `/api/cards/feed?${п.toString()}`,
  )
  const rows = о.rows ?? []
  const начало = Number(о.offset ?? offset)
  const всего = Number(о.total ?? rows.length)
  return {
    rows,
    total: всего,
    limit: Number(о.limit ?? СТРАНИЦА_ЛЕНТЫ),
    offset: начало,
    // Ядро next не отдаёт — считаем по total. Пустая страница дальше не листается.
    next: rows.length > 0 && начало + rows.length < всего ? начало + rows.length : null,
  }
}

/** Свои поля карточки: определения конструктора + записанные значения. */
export async function нашиПоляКарточки(
  сущность: СущностьКарточки, ownId: number,
): Promise<НашиПоля> {
  const п = new URLSearchParams({ entity: сущность, id: String(ownId) })
  const о = await читать<{ custom?: СвоёПоле[]; values?: Record<string, unknown> }>(
    `/api/cards/fields?${п.toString()}`,
  )
  return { custom: (о.custom ?? []).filter((x) => !x.archived), values: о.values ?? {} }
}

/** Комментарий к карточке. Ядро возвращает готовую строку ленты — дописываем её без перечитки. */
export async function добавитьКомментарий(
  сущность: СущностьКарточки, ownId: number, текст: string,
): Promise<СтрокаЛенты> {
  const о = await писать<{ event?: СтрокаЛенты }>('comment-add', {
    entity: сущность, id: ownId, text: текст,
  })
  return о.event ?? { id: `c-${Date.now()}`, kind: 'comment', origin: 'crm', text: текст }
}

/** Значение своего поля. Пустое значение — «очистить» (так понимает слой записи). */
export const сохранитьЗначение = (
  сущность: СущностьКарточки, ownId: number, код: string, значение: unknown,
) => писать<{ code: string; value: unknown; changed: boolean }>('field-value-save', {
  entity: сущность, id: ownId, code: код, value: значение,
})

// ─────────────────────────── хуки ───────────────────────────

/**
 * Лента со страницами «Показать ещё». Пока свой id неизвестен (ownId null) — в сеть
 * не ходим вовсе: причина отсутствия ленты известна заранее и словами.
 * retry: false — 404 и 403 от повтора не изменятся.
 */
export const useЛентаКарточки = (сущность: СущностьКарточки, ownId: number | null) =>
  useInfiniteQuery({
    queryKey: ['card-feed', сущность, ownId],
    enabled: ownId !== null,
    initialPageParam: 0,
    queryFn: ({ pageParam }) => лентаКарточки(сущность, ownId!, pageParam),
    getNextPageParam: (последняя) => последняя.next ?? undefined,
    staleTime: 30_000,
    retry: false,
  })

export const КЛЮЧ_НАШИХ_ПОЛЕЙ = 'card-own-fields'

export const useНашиПоля = (сущность: СущностьКарточки, ownId: number | null) =>
  useQuery({
    queryKey: [КЛЮЧ_НАШИХ_ПОЛЕЙ, сущность, ownId],
    enabled: ownId !== null,
    queryFn: () => нашиПоляКарточки(сущность, ownId!),
    staleTime: 30_000,
    retry: false,
  })

// ─────────────────────────── показ ───────────────────────────

/** Событие для Timeline — те же поля, что TimelineEvent, но text строкой (чистые данные). */
export interface СобытиеЛенты {
  id: string | number
  at?: string | null
  kind: string
  author?: string
  text: string
  origin?: 'crm' | 'bitrix'
}

/**
 * Подпись вида события. comment/field/stage Timeline знает сам; остальное переводим:
 * у активности Bitrix вид лежит в data.activity («звонок», «письмо», «встреча»),
 * веха-сделка ядра (kind='system') подписывается «веха».
 */
function видСобытия(с: СтрокаЛенты): string {
  if (с.kind === 'activity') return String(с.data?.activity ?? 'дело')
  if (с.kind === 'system') return 'веха'
  return с.kind
}

/**
 * Страницы ленты ядра → события Timeline. Порядок не трогаем — сортирует Timeline.
 * Повторы по id отбрасываются: между двумя запросами страниц в ленту могло добавиться
 * событие, и строки следующей страницы «съезжают» — одна и та же приезжает дважды.
 */
export function событияЛенты(страницы: СтраницаЛенты[] | undefined): СобытиеЛенты[] {
  const из: СобытиеЛенты[] = []
  const были = new Set<string>()
  for (const стр of страницы ?? []) {
    for (const с of стр.rows) {
      const ключ = String(с.id)
      if (были.has(ключ)) continue
      были.add(ключ)
      из.push({
        id: с.id,
        at: с.ts ?? null,
        kind: видСобытия(с),
        author: с.author?.name || undefined,
        text: String(с.text ?? ''),
        origin: с.origin,
      })
    }
  }
  return из
}

/**
 * Свои поля → строки FieldRow. Виды совпадают дословно (см. СвоёПоле.kind), варианты
 * списка — строки, у FieldRow опция {id, name}: значение и подпись здесь одно и то же.
 */
export function нашиПоляКакСтроки(п: НашиПоля | undefined): CardField[] {
  return (п?.custom ?? []).map((поле) => {
    const значение = п?.values?.[поле.code]
    return {
      code: поле.code,
      label: поле.label,
      type: поле.kind,
      value: значение ?? null,
      origin: 'crm' as const,
      ...(поле.kind === 'select'
        ? { options: поле.options.map((o) => ({ id: o, name: o })) }
        : {}),
      ...(поле.kind === 'link' && typeof значение === 'string' && значение
        ? { href: значение }
        : {}),
    }
  })
}

/** Текст ошибки — словами, для заметки под лентой или у поля. */
export const словами = (e: unknown): string =>
  (e instanceof Error ? e.message : String(e))

/**
 * Почему живая часть карточки недоступна — одна фраза на ленту и на свои поля.
 * undefined — доступна. Три случая различаются нарочно: «читаем зеркало» — настройка,
 * «не перенесена» — состояние записи, всё прочее — отказ ядра его же словами.
 */
export function почемуНедоступно(ownId: number | null, ошибка: unknown): string | undefined {
  if (ownId === null) return источник() === 'mirror' ? ЧИТАЕМ_ЗЕРКАЛО : НЕ_ПЕРЕНЕСЕНА
  if (ошибка instanceof КарточкаНеПеренесена) return НЕ_ПЕРЕНЕСЕНА
  if (ошибка) return `Ядро не отдало: ${словами(ошибка)}`
  return undefined
}
